import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Tag, ArrowLeft, User, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve image URLs - handles both relative and absolute paths
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const NewsArticle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/news/${id}`);
      setArticle(response.data);
      
      // Fetch related articles (same category)
      const allNews = await axios.get(`${BACKEND_URL}/api/news?published_only=true`);
      const related = allNews.data
        .filter(a => a.id !== id && a.category === response.data.category)
        .slice(0, 3);
      setRelatedArticles(related);
    } catch (error) {
      setError('Article not found');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007A7A] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-6">The article you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/news')} className="bg-[#007A7A] hover:bg-[#007A7A]/90">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to News
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="news-article-page">
      {/* Hero Section with Article Image */}
      <section className="relative py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          {article.image ? (
            <img 
              src={resolveImageUrl(article.image)}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <img 
              src="https://images.pexels.com/photos/518543/pexels-photo-518543.jpeg?auto=compress&cs=tinysrgb&w=1600"
              alt="News"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A345E]/90 to-[#007A7A]/85"></div>
        </div>
        <div className="container mx-auto max-w-4xl relative z-10">
          <Button 
            onClick={() => navigate('/news')} 
            variant="outline" 
            className="mb-6 bg-white/10 border-white/30 text-white hover:bg-white/20"
            data-testid="back-to-news-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to News
          </Button>
          
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-white/20 text-white">
              <Tag className="w-4 h-4 mr-2" />
              {article.category}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">{article.title}</h1>
          
          <div className="flex flex-wrap items-center gap-6 text-white/90">
            <div className="flex items-center">
              <User className="w-5 h-5 mr-2" />
              <span>By {article.author || 'Admin'}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              <span>{formatDate(article.published_date)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3">
                {/* Excerpt/Lead */}
                <p className="text-xl text-gray-700 leading-relaxed mb-8 font-medium border-l-4 border-[#007A7A] pl-6">
                  {article.excerpt}
                </p>

                {/* Full Content */}
                <div className="prose prose-lg max-w-none">
                  {article.content.split('\n').map((paragraph, index) => (
                    paragraph.trim() && (
                      <p key={`paragraph-${index}-${paragraph.slice(0, 20)}`} className="text-gray-700 leading-relaxed mb-6">
                        {paragraph}
                      </p>
                    )
                  ))}
                </div>

                {/* Share Section */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex items-center gap-4">
                    <span className="text-gray-700 font-medium flex items-center">
                      <Share2 className="w-5 h-5 mr-2" />
                      Share this article:
                    </span>
                    <div className="flex gap-2">
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center text-white transition-colors"
                      >
                        <Facebook className="w-5 h-5" />
                      </a>
                      <a
                        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-sky-500 hover:bg-sky-600 rounded-full flex items-center justify-center text-white transition-colors"
                      >
                        <Twitter className="w-5 h-5" />
                      </a>
                      <a
                        href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(article.title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-blue-700 hover:bg-blue-800 rounded-full flex items-center justify-center text-white transition-colors"
                      >
                        <Linkedin className="w-5 h-5" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                {relatedArticles.length > 0 && (
                  <div className="sticky top-24">
                    <h3 className="text-lg font-bold text-[#0A345E] mb-4">Related Articles</h3>
                    <div className="space-y-4">
                      {relatedArticles.map((related) => (
                        <Link 
                          key={related.id} 
                          to={`/news/${related.id}`}
                          className="block group"
                        >
                          <Card className="hover:shadow-lg transition-shadow duration-200 bg-[#F5F2E8] border-0">
                            <CardContent className="p-4">
                              <h4 className="font-medium text-[#0A345E] group-hover:text-[#007A7A] transition-colors line-clamp-2 mb-2">
                                {related.title}
                              </h4>
                              <p className="text-sm text-gray-500">
                                {formatDate(related.published_date)}
                              </p>
                            </CardContent>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-[#F5F2E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-[#0A345E] mb-4">Stay Connected</h2>
          <p className="text-gray-600 mb-6">Want to stay updated with more news and stories from Gateway Inc.?</p>
          <div className="flex justify-center gap-4">
            <Button 
              onClick={() => navigate('/news')} 
              variant="outline"
              className="border-[#007A7A] text-[#007A7A]"
            >
              View All News
            </Button>
            <Button 
              onClick={() => navigate('/donate')} 
              className="bg-[#007A7A] hover:bg-[#007A7A]/90"
            >
              Support Our Mission
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsArticle;
